export type EntitySelectItem = {
  id: string;
  title?: string;
  url?: string;
  parentId?: string;
};

type SelectedItemChangedEvent = CustomEvent<{
  value?: EntitySelectItem;
}>;

/**
 * Navigate when the admin header entity picker selects an item (uses item.url from items JSON).
 */
export function navigateForSelectedEntityItem(item: EntitySelectItem | undefined | null): boolean {
  if (!item?.url) {
    return false;
  }

  const targetPath = item.url.startsWith('/')
    ? item.url.split('?')[0].split('#')[0]
    : (() => {
        try {
          return new URL(item.url, globalThis.location.origin).pathname;
        } catch {
          return '';
        }
      })();

  if (!targetPath) {
    return false;
  }

  if (targetPath === globalThis.location.pathname) {
    return false;
  }

  // Dynamic import keeps this module free of a hard cycle with admin boot.
  void import('./common/admin-nav-loading').then(({navigateAdminPage}) => {
    navigateAdminPage(targetPath);
  }).catch(() => {
    globalThis.location.assign(targetPath);
  });
  return true;
}

/**
 * Header org picker hydrates a preset value on load and can emit
 * `selected-item-changed` as an echo. Only navigate after a real user gesture
 * AND when the selected id differs from the value rendered by the server.
 */
export function shouldNavigateOrganisationSelection(
  userHasInteracted: boolean,
  selectedId: string | undefined,
  initialValue: string
): boolean {
  if (!userHasInteracted) {
    return false;
  }

  if (!selectedId) {
    return false;
  }

  return String(selectedId) !== String(initialValue);
}

export async function setupOrganisationDropdownListeners(): Promise<void> {
  await customElements.whenDefined('cbar-entity-select');

  const organisationDropdown = document.querySelector(
    '#organisation_select'
  ) as CbarEntitySelectElement | null;

  if (!organisationDropdown || organisationDropdown.dataset.orgNavBound === 'true') {
    return;
  }

  organisationDropdown.dataset.orgNavBound = 'true';

  const initialValue = organisationDropdown.getAttribute('value') ?? '';
  let userHasInteracted = false;
  const markInteracted = () => {
    userHasInteracted = true;
  };

  // pointerdown/keydown cover mouse, touch, and keyboard without treating
  // programmatic focus (if any) as a navigation intent.
  organisationDropdown.addEventListener('pointerdown', markInteracted);
  organisationDropdown.addEventListener('keydown', markInteracted);

  // selected-item-changed fires before the host `value` property updates — use detail.value.
  organisationDropdown.addEventListener('selected-item-changed', (event: Event) => {
    const item = (event as SelectedItemChangedEvent).detail?.value;
    if (
      !shouldNavigateOrganisationSelection(userHasInteracted, item?.id, initialValue)
    ) {
      return;
    }

    navigateForSelectedEntityItem(item);
  });
}

function setupHeaderSearch() {
  document.addEventListener('search-submitted', (e: Event) => {
    const detail = (e as CustomEvent<string>).detail;
    if (detail) {
      window.location.href =
        '/admin/searchentities?search_string=' + encodeURIComponent(detail);
    }
  });
}

export function handleHeaderSidenav() {
  setupHeaderSearch();
}
